/**
 * logger.ts — NovelForge 统一日志系统
 *
 * 设计目标：
 * 1. 替代散落的 console.warn/error/debug，统一格式便于排查
 * 2. 每条日志带 [模块名] + 时间戳 + 级别，grep 友好
 * 3. 生产环境自动降级（只输出 warn/error），开发环境全量输出
 * 4. 支持结构化 context 数据，便于 JSON 过滤
 * 5. 内存环形缓冲区保留最近 500 条，E2E/调试时可通过 globalThis.__logger 导出
 *
 * 使用方式：
 *   import { createLogger } from '@/shared/utils/logger';
 *   const log = createLogger('ContextEngine');
 *   log.info('Layers assembled', { tokenCount: 3200, fillRate: 0.85 });
 *   log.warn('L3 empty — cache may be stale');
 *   log.error('LLM call failed', { error: err.message, attempt: 2 });
 */

// ── Types ──────────────────────────────────────────────────────────

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface LogEntry {
  /** ISO timestamp */
  ts: string;
  /** Log level */
  level: LogLevel;
  /** Module/component name */
  module: string;
  /** Human-readable message */
  msg: string;
  /** Optional structured data */
  data?: Record<string, unknown>;
}

export interface Logger {
  debug(msg: string, data?: Record<string, unknown>): void;
  info(msg: string, data?: Record<string, unknown>): void;
  warn(msg: string, data?: Record<string, unknown>): void;
  error(msg: string, data?: Record<string, unknown>): void;
}

// ── Configuration ──────────────────────────────────────────────────

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

/** Max entries in the ring buffer */
const BUFFER_SIZE = 500;

/** Minimum level for console output (production vs dev) */
function isDevMode(): boolean {
  try {
    return !!(import.meta.env?.DEV);
  } catch {
    return false;
  }
}

const MIN_CONSOLE_LEVEL: LogLevel = isDevMode() ? 'debug' : 'warn';

// ── Ring Buffer ────────────────────────────────────────────────────

const buffer: LogEntry[] = [];
let bufferIndex = 0;

function pushEntry(entry: LogEntry): void {
  if (buffer.length < BUFFER_SIZE) {
    buffer.push(entry);
  } else {
    buffer[bufferIndex % BUFFER_SIZE] = entry;
  }
  bufferIndex++;
}

// ── Console output ─────────────────────────────────────────────────

const LEVEL_STYLE: Record<LogLevel, string> = {
  debug: 'color: #888',
  info: 'color: #4a9eff',
  warn: 'color: #f59e0b; font-weight: bold',
  error: 'color: #ef4444; font-weight: bold',
};

const LEVEL_CONSOLE_FN: Record<LogLevel, 'debug' | 'info' | 'warn' | 'error'> = {
  debug: 'debug',
  info: 'info',
  warn: 'warn',
  error: 'error',
};

function writeToConsole(entry: LogEntry): void {
  if (LOG_LEVELS[entry.level] < LOG_LEVELS[MIN_CONSOLE_LEVEL]) return;

  const prefix = `%c[${entry.ts.slice(11, 23)}] [${entry.level.toUpperCase().padEnd(5)}] [${entry.module}]`;
  const fn = LEVEL_CONSOLE_FN[entry.level];
  const style = LEVEL_STYLE[entry.level];

  if (entry.data && Object.keys(entry.data).length > 0) {
    console[fn](prefix, style, entry.msg, entry.data);
  } else {
    console[fn](prefix, style, entry.msg);
  }
}

// ── Public API ─────────────────────────────────────────────────────

/**
 * Create a scoped logger for a module/component.
 *
 * @param module - Module name (e.g. 'ContextEngine', 'useChapterConfirm', 'LLMGateway')
 */
export function createLogger(module: string): Logger {
  function log(level: LogLevel, msg: string, data?: Record<string, unknown>): void {
    const entry: LogEntry = {
      ts: new Date().toISOString(),
      level,
      module,
      msg,
      data,
    };
    pushEntry(entry);
    writeToConsole(entry);
  }

  return {
    debug: (msg, data) => log('debug', msg, data),
    info: (msg, data) => log('info', msg, data),
    warn: (msg, data) => log('warn', msg, data),
    error: (msg, data) => log('error', msg, data),
  };
}

/**
 * Get all buffered log entries (most recent BUFFER_SIZE entries, chronological order).
 * Useful for E2E test diagnostics and bug reports.
 */
export function getLogBuffer(): LogEntry[] {
  if (buffer.length < BUFFER_SIZE) return [...buffer];
  // Ring buffer — reorder to chronological
  const start = bufferIndex % BUFFER_SIZE;
  return [...buffer.slice(start), ...buffer.slice(0, start)];
}

/**
 * Get log entries filtered by criteria.
 */
export function queryLogs(opts: {
  level?: LogLevel;
  module?: string;
  since?: string;
  contains?: string;
}): LogEntry[] {
  const minLevel = opts.level ? LOG_LEVELS[opts.level] : 0;
  return getLogBuffer().filter((e) => {
    if (LOG_LEVELS[e.level] < minLevel) return false;
    if (opts.module && e.module !== opts.module) return false;
    if (opts.since && e.ts < opts.since) return false;
    if (opts.contains && !e.msg.includes(opts.contains) && !JSON.stringify(e.data ?? {}).includes(opts.contains)) return false;
    return true;
  });
}

/**
 * Clear the ring buffer.
 */
export function clearLogBuffer(): void {
  buffer.length = 0;
  bufferIndex = 0;
}

// ── Expose for E2E and debugging ───────────────────────────────────

if (typeof globalThis !== 'undefined') {
  (globalThis as Record<string, unknown>).__logger = {
    getBuffer: getLogBuffer,
    query: queryLogs,
    clear: clearLogBuffer,
    create: createLogger,
  };
}
